<template>
  <component
    :is="isDesktop ? VDialog : VBottomSheet"
    :model-value="check.open"
    scrollable
    :max-width="isDesktop ? DESKTOP_WIDTH : undefined"
    @update:model-value="(value) => (value ? check.show() : check.close())"
  >
    <v-card class="ai-sheet" :class="{ 'ai-sheet--desktop': isDesktop }">
      <v-card-title class="d-flex align-center">
        <v-icon start>mdi-creation-outline</v-icon>
        AI-check
        <v-spacer />
        <v-btn
          icon="mdi-close"
          variant="text"
          aria-label="AI-check sluiten"
          @click="check.close()"
        />
      </v-card-title>

      <v-card-text class="ai-sheet__body px-3">
        <p class="text-caption text-medium-emphasis mb-3">
          Dit zijn voorstellen. Er verandert alleen iets als je het zelf
          overneemt.
        </p>

        <v-alert
          v-if="check.isStale && !check.checking"
          type="info"
          variant="tonal"
          density="compact"
          class="mb-3"
        >
          De tekst is gewijzigd sinds de controle.
          <template #append>
            <v-btn size="small" variant="text" @click="check.run()">
              Opnieuw controleren
            </v-btn>
          </template>
        </v-alert>

        <v-expansion-panels v-model="openPanels" multiple variant="accordion">
          <v-expansion-panel
            v-for="section in check.sectionList"
            :key="section.key"
            :value="section.key"
          >
            <v-expansion-panel-title>
              <v-icon start size="small">{{ section.icon }}</v-icon>
              {{ section.title }}
              <v-progress-circular
                v-if="section.status === 'loading'"
                indeterminate
                size="16"
                width="2"
                class="ml-3"
              />
              <v-chip
                v-else-if="section.status === 'error'"
                size="x-small"
                color="error"
                variant="tonal"
                class="ml-3"
              >
                Mislukt
              </v-chip>
              <v-chip
                v-else-if="section.count"
                size="x-small"
                color="primary"
                variant="flat"
                class="ml-3"
              >
                {{ section.count }}
              </v-chip>
              <span v-else class="text-caption text-medium-emphasis ml-3">
                {{ emptyLabel(section) }}
              </span>
            </v-expansion-panel-title>

            <v-expansion-panel-text>
              <v-skeleton-loader
                v-if="section.status === 'loading'"
                type="list-item-two-line"
              />

              <div v-else-if="section.status === 'error'">
                <p class="text-body-2 mb-2">{{ section.message }}</p>
                <v-btn
                  variant="tonal"
                  prepend-icon="mdi-refresh"
                  @click="check.retry(section.key)"
                >
                  Opnieuw proberen
                </v-btn>
              </div>

              <AiReviewPanel
                v-else-if="section.key === 'report_review' && check.review"
                :review="check.review"
                :can-apply="check.canApplyRewrite"
                :applied="check.reviewApplied"
                :stale="check.isStale"
                :rating="check.reviewRating"
                @apply="check.applyRewrite()"
                @rate="check.rateReview($event)"
              />

              <template v-else-if="section.key === 'mention_link'">
                <AiMentionLinks
                  v-if="check.mentionLinks.length"
                  :links="check.mentionLinks"
                  @accept="check.acceptMentionLink($event)"
                  @reject="check.rejectMentionLink($event)"
                />
                <p v-else class="text-body-2 text-medium-emphasis">
                  Geen namen gevonden om te koppelen.
                </p>
              </template>

              <template v-else-if="section.key === 'follow_up_suggest'">
                <AiFollowUpSuggestions
                  v-if="hasOpenOrAddedTasks"
                  :rows="check.followUpRows"
                  @update="(id, patch) => check.updateFollowUp(id, patch)"
                  @add="check.addFollowUp($event)"
                  @reject="check.rejectFollowUp($event)"
                />
                <p v-else class="text-body-2 text-medium-emphasis">
                  Geen vervolgacties gevonden in de tekst.
                </p>
              </template>

              <template v-else-if="section.key === 'trait_suggest'">
                <AiTraitSuggestions
                  v-if="check.traitSuggestions.length"
                  :suggestions="check.traitSuggestions"
                  :selected-ids="selectedTraitIds"
                  @accept="check.acceptTrait($event)"
                  @reject="check.rejectTrait($event)"
                />
                <p v-else class="text-body-2 text-medium-emphasis">
                  Geen passende kenmerken gevonden.
                </p>
              </template>
            </v-expansion-panel-text>
          </v-expansion-panel>
        </v-expansion-panels>
      </v-card-text>

      <v-card-actions class="ai-sheet__actions">
        <v-spacer v-if="isDesktop" />
        <v-btn
          :block="!isDesktop"
          color="primary"
          variant="flat"
          :size="isDesktop ? 'default' : 'large'"
          class="ai-sheet__done"
          @click="check.close()"
        >
          Klaar
        </v-btn>
      </v-card-actions>
    </v-card>
  </component>
</template>

<script setup>
/**
 * The results of one AI-check of a report draft: a section per skill (text,
 * mentions, tasks, traits). On a phone it is a bottom sheet: one column, large touch
 * targets, a fixed full-width "Klaar" button. On a wide screen it is a dialog of the
 * same width as the report form, with compact buttons aligned to the right.
 * State and actions come from useAiDraftCheck.
 */
import { ref, computed, watch } from "vue";
import { useDisplay } from "vuetify";
import { VBottomSheet, VDialog } from "vuetify/components";
import AiReviewPanel from "./AiReviewPanel.vue";
import AiMentionLinks from "./AiMentionLinks.vue";
import AiFollowUpSuggestions from "./AiFollowUpSuggestions.vue";
import AiTraitSuggestions from "./AiTraitSuggestions.vue";

const props = defineProps({
  // reactive(useAiDraftCheck(...))
  check: { type: Object, required: true },
  // Trait ids currently on the report
  selectedTraitIds: { type: Array, default: () => [] },
});

// As wide as the report form's own dialog, so it reads as a step in the same flow.
const DESKTOP_WIDTH = 640;

const { mdAndUp: isDesktop } = useDisplay();
const openPanels = ref([]);

const hasOpenOrAddedTasks = computed(() =>
  props.check.followUpRows.some((row) => row.state !== "rejected"),
);

// Header text for a section without open suggestions: nothing was suggested, or
// everything has been handled.
const emptyLabel = (section) =>
  section.total > 0 ? "Verwerkt" : "Geen voorstellen";

// While sections load they are all open; once every section has its result, only
// the ones with something to show stay open. Watches the sections themselves, not
// the request state: results are stored a tick after the request finishes.
const loading = computed(() =>
  props.check.sectionList.some((section) => section.status === "loading"),
);

watch(
  loading,
  (isLoading) => {
    openPanels.value = props.check.sectionList
      .filter(
        (section) =>
          isLoading || section.count > 0 || section.status === "error",
      )
      .map((section) => section.key);
  },
  { flush: "post" },
);
</script>

<style scoped>
.ai-sheet {
  max-height: 88vh;
}

.ai-sheet__body {
  overflow-y: auto;
}

.ai-sheet__actions {
  position: sticky;
  bottom: 0;
  background: rgb(var(--v-theme-surface));
  border-top: 1px solid rgba(var(--v-theme-on-surface), 0.12);
  padding-bottom: max(8px, env(safe-area-inset-bottom));
}

/* Wide screens: buttons keep their own width and sit on the right. */
.ai-sheet--desktop :deep(.ai-suggestion-actions) {
  justify-content: flex-end;
}

.ai-sheet--desktop :deep(.ai-suggestion-actions .v-btn) {
  flex-grow: 0 !important;
}

.ai-sheet--desktop .ai-sheet__actions {
  padding: 12px 16px;
}

.ai-sheet--desktop .ai-sheet__done {
  min-width: 120px;
}
</style>
